import React, { useState, useEffect } from 'react';

import { Grid } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { trackEvent } from 'on_web/helpers/userTrackingEventsHelper';

import DiseasesIntro from 'on_web/components/onboarding/diseases/DiseasesIntro';
import DiseasesStep1 from 'on_web/components/onboarding/diseases/DiseasesStep1';
import DiseasesStep2 from 'on_web/components/onboarding/diseases/DiseasesStep2';
import DiseasesStep3 from 'on_web/components/onboarding/diseases/DiseasesStep3';
import DiseasesStep4 from 'on_web/components/onboarding/diseases/DiseasesStep4';
import DiseasesStep6 from 'on_web/components/onboarding/diseases/DiseasesStep6';
import DiseasesStep7 from 'on_web/components/onboarding/diseases/DiseasesStep7';
import DiseasesStep5 from 'on_web/components/onboarding/diseases/DiseasesStep5';
import PageItemContainer from 'on_web/components/shared/Common/PageItemContainer/PageItemContainer';

import {
  ROUTE_ONBOARDING_INTRO,
  ROUTE_ONBOARDING_LIFESTYLE,
  ROUTE_ONBOARDING_ENVIRONMENT,
  ROUTE_HEALTH_PROFILE,
} from 'routes';
import { getRedirectRoute } from '../helpers/redirectHelper';

import UserProfileContext from 'on_web/context/userProfileContext';
import useUsersApi from '../hooks/useUsersApi';
import {
  objectIsEmpty,
  objectHasAllValuesZero,
} from 'on_web/helpers/customHelpers';

import { getAceValuesObj, getArbValuesObj } from 'on_web/helpers/questions';

function OnboardingDiseasesPage(props) {
  const context = React.useContext(UserProfileContext);
  const [
    {
      response,
      loading,
      responseCovidPastInfections,
      loadingCovidPastInfections,
      responseCovidComorbidities,
      responseCovidSymptoms,
      responseDiseases,
      responseACE,
      loadingACE,
      responseARB,
      loadingARB,
      getUserData,
      saveUserData,
      getCovidPastInfections,
      saveCovidPastInfections,
      getCovidComorbidities,
      saveCovidComorbidities,
      getCovidSymptoms,
      saveCovidSymptoms,
      getDiseases,
      saveDiseases,
      getACE,
      saveACE,
      getARB,
      saveARB,
    },
  ] = useUsersApi();
  const [visibleStep, setVisibleStep] = useState(
    props.location.state ? null : 0
  );
  const [editMode, setEditMode] = useState(false);
  const [continueInEditMode, setContinueInEditMode] = useState(
    context && context.continueInEditMode ? context.continueInEditMode : false
  );

  useEffect(() => {
    let userData;
    let covidPastInfections;
    let covidComorbidities;
    let covidSymptopms;
    let diseases;
    let aceData;
    getUserData()
      .then(userDataResponse => {
        userData = userDataResponse;
        return getCovidPastInfections();
      })
      .then(covidPastInfectionsResponse => {
        covidPastInfections = covidPastInfectionsResponse;
        return getCovidComorbidities();
      })
      .then(covidComorbiditiesResponse => {
        covidComorbidities = covidComorbiditiesResponse;
        return getCovidSymptoms();
      })
      .then(covidSymptopmsResponse => {
        covidSymptopms = covidSymptopmsResponse;
        return getDiseases();
      })
      .then(diseasesResponse => {
        diseases = diseasesResponse;
        return getACE();
      })
      .then(aceResponse => {
        aceData = aceResponse;
        return getARB();
      })
      .then(arbData => {
        if (
          userData != null &&
          covidPastInfections != null &&
          covidComorbidities != null &&
          covidSymptopms != null &&
          diseases != null &&
          arbData != null &&
          aceData != null
        ) {
          if (props.location.state && props.location.state.editMode) {
            // if edit mode is requested, jump to requested step
            setEditMode(true);
            setVisibleStep(props.location.state.editStep);
            return;
          } else {
            setEditMode(false);
          }

          if (
            visibleStep == null &&
            props.location.state &&
            props.location.state.startFromLast
          ) {
            return setVisibleStep(7);
          } else if (Object.keys(userData.user_metadata).length === 0) {
            props.history.push(ROUTE_ONBOARDING_INTRO);
          } else if (covidPastInfections.tested_for_covid_19 == null) {
            return setVisibleStep(0);
          } else if (
            visibleStep == null &&
            props.location.state &&
            props.location.state.startFromFirst
          ) {
            setVisibleStep(1);
          } else if (
            covidPastInfections.tested_for_covid_19_antibodies == null
          ) {
            return setVisibleStep(2);
          } else if (
            covidPastInfections.contact_with_individual_covid_19_diagnosis_symptoms ==
            null
          ) {
            return setVisibleStep(3);
          } else if (
            objectIsEmpty(covidComorbidities) &&
            userData.user_metadata.covidComorbiditiesAreNone == null
          ) {
            return setVisibleStep(4);
          } else if (covidComorbidities.hypertension === 1) {
            return setVisibleStep(5);
          } else if (objectIsEmpty(covidSymptopms)) {
            return setVisibleStep(6);
          } else if (
            objectHasAllValuesZero(diseases) &&
            objectIsEmpty(userData.user_metadata.diseasesData)
          ) {
            return setVisibleStep(7);
          } else {
            const route = getRedirectRoute(
              userData.user_metadata,
              context.showBasicQuestions
            );
            props.history.push(route);
          }
        }
      });
  }, [
    getUserData,
    getCovidPastInfections,
    getCovidComorbidities,
    getCovidSymptoms,
    getDiseases,
    getACE,
    getARB,
  ]);

  const onWelcome = () => {
    // when intro is displayed, it is time for another step
    setVisibleStep(visibleStep + 1);
  };

  const goToNextStep = (userMetadata, userAce, userArb) => {
    if (userAce) {
      saveACE(userAce).then(resp => {
        if (resp != null) {
          if (userArb) {
            handleArb(userMetadata, userArb);
          } else {
            handleUserMetadata(userMetadata);
          }
        }
      });
    } else {
      if (userArb) {
        handleArb(userMetadata, userArb);
      } else {
        handleUserMetadata(userMetadata);
      }
    }
  };

  const handleArb = (userMetadata, userArb) => {
    saveARB(userArb).then(resp => {
      if (resp != null) {
        handleUserMetadata(userMetadata);
      }
    });
  };

  const handleNextClick = (
    userMetaData,
    covidPastInfections,
    covidComorbidities,
    covidSymptopms,
    diseases
  ) => {
    if (covidPastInfections) {
      saveCovidPastInfections(covidPastInfections).then(resp => {
        if (resp != null) {
          handleCovidComorbidities(
            covidComorbidities,
            covidSymptopms,
            userMetaData,
            diseases
          );
        }
      });
    } else {
      handleCovidComorbidities(
        covidComorbidities,
        covidSymptopms,
        userMetaData,
        diseases
      );
    }
  };

  const handleCovidComorbidities = (
    covidComorbidities,
    covidSymptopms,
    userMetaData,
    diseases
  ) => {
    if (covidComorbidities) {
      userMetaData.skipMedications =
        covidComorbidities.hypertension === 0 ? true : false;
      saveCovidComorbidities(covidComorbidities).then(resp => {
        if (resp != null) {
          handleCovidSymptoms(covidSymptopms, userMetaData, diseases);
        }
      });
    } else {
      handleCovidSymptoms(covidSymptopms, userMetaData, diseases);
    }
  };

  const handleCovidSymptoms = (covidSymptopms, userMetaData, diseases) => {
    if (covidSymptopms) {
      saveCovidSymptoms(covidSymptopms).then(resp => {
        if (resp != null) {
          handleDiseases(userMetaData, diseases);
        }
      });
    } else {
      handleDiseases(userMetaData, diseases);
    }
  };

  const handleDiseases = (userMetaData, diseases) => {
    if (diseases) {
      saveDiseases(diseases).then(resp => {
        if (resp != null) {
          handleUserMetadata(userMetaData);
        }
      });
    } else {
      handleUserMetadata(userMetaData);
    }
  };

  const handleUserMetadata = userMetaData => {
    if (userMetaData) {
      response.user_metadata = userMetaData;
    }
    if (visibleStep + 1 > 7) {
      response.user_metadata.onboarding = {
        ...response.user_metadata.onboarding,
        diseasesFinished: true,
      };
      saveUserData(response).then(resp => {
        if (resp != null) {
          trackEvent(
            'Completed DISEASES',
            'User has completed DISEASES section onboarding'
          );
          if (editMode) {
            props.history.push(ROUTE_HEALTH_PROFILE);
          } else {
            props.history.push({
              pathname: ROUTE_ONBOARDING_ENVIRONMENT,
              state: {
                startFromFirst: true,
              },
            });
          }
        }
      });
    } else {
      if (userMetaData) {
        if (visibleStep === 4) {
          if (userMetaData.skipMedications) {
            saveACE(getAceValuesObj())
              .then(() => saveARB(getArbValuesObj()))
              .then(() => {
                saveUserData(response).then(resp => {
                  if (resp != null) {
                    if (editMode) {
                      props.history.push(ROUTE_HEALTH_PROFILE);
                      setContinueInEditMode(false);
                    } else {
                      setVisibleStep(visibleStep + 2);
                    }
                  }
                });
              });
          } else {
            saveUserData(response).then(resp => {
              if (resp != null) {
                if (editMode) {
                  setContinueInEditMode(true);
                }
                setVisibleStep(visibleStep + 1);
              }
            });
          }
        } else {
          if (userMetaData.bloodPressureMedication === '2') {
            response.user_metadata.medicationsAceNone = true;
            response.user_metadata.medicationsArbNone = true;
            saveACE(getAceValuesObj())
              .then(() => saveARB(getArbValuesObj()))
              .then(() => saveUserData(response))
              .then(resp => {
                if (resp != null) {
                  if (editMode) {
                    props.history.push(ROUTE_HEALTH_PROFILE);
                  } else {
                    setVisibleStep(visibleStep + 1);
                  }
                }
              });
          } else {
            saveUserData(response).then(resp => {
              if (resp != null) {
                if (editMode) {
                  props.history.push(ROUTE_HEALTH_PROFILE);
                } else {
                  setVisibleStep(visibleStep + 1);
                }
              }
            });
          }
        }
      } else {
        if (editMode) {
          props.history.push(ROUTE_HEALTH_PROFILE);
        } else {
          setVisibleStep(visibleStep + 1);
        }
      }
    }
  };

  const handleBackClick = () => {
    if (visibleStep === 1) {
      props.history.push({
        pathname: ROUTE_ONBOARDING_LIFESTYLE,
        state: {
          startFromLast: true,
        },
      });
    } else if (editMode && visibleStep === 4) {
      props.history.push(ROUTE_HEALTH_PROFILE);
    } else if (
      visibleStep === 6 &&
      responseCovidComorbidities &&
      responseCovidComorbidities.hypertension === 0
    ) {
      setVisibleStep(visibleStep - 2);
    } else {
      setVisibleStep(visibleStep - 1);
    }
  };

  return (
    <Grid
      container={useMediaQuery('(min-width:1000px)')}
      direction="row"
      justify="center"
      alignItems="center"
    >
      {loading === false &&
        loadingCovidPastInfections === false &&
        response &&
        responseCovidPastInfections &&
        responseDiseases &&
        visibleStep != null &&
        loadingACE === false &&
        responseACE &&
        loadingARB === false &&
        responseARB && (
          <UserProfileContext.Provider
            value={{
              userMetaData: response.user_metadata,
              covidPastInfections: responseCovidPastInfections,
              covidComorbidities: responseCovidComorbidities,
              covidSymptopms: responseCovidSymptoms,
              diseases: responseDiseases,
              showBasicQuestions:
                context && context.showBasicQuestions === true
                  ? context.showBasicQuestions
                  : false,
              userAce: responseACE,
              userArb: responseARB,
              editMode: editMode,
              continueInEditMode: continueInEditMode,
            }}
          >
            {/** Separate welcome rendering condition from others. */}
            {visibleStep === 0 && (
              <PageItemContainer>
                <DiseasesIntro onWelcome={onWelcome} />
              </PageItemContainer>
            )}
            {visibleStep === 1 && (
              <PageItemContainer>
                <DiseasesStep1
                  onClickBack={() => handleBackClick()}
                  onClickNext={(userMetaData, covidPastInfections) =>
                    handleNextClick(userMetaData, covidPastInfections)
                  }
                />
              </PageItemContainer>
            )}
            {visibleStep === 2 && (
              <PageItemContainer>
                <DiseasesStep2
                  onClickBack={() => handleBackClick()}
                  onClickNext={(userMetaData, covidPastInfections) =>
                    handleNextClick(userMetaData, covidPastInfections)
                  }
                />
              </PageItemContainer>
            )}
            {visibleStep === 3 && (
              <PageItemContainer>
                <DiseasesStep3
                  onClickBack={() => handleBackClick()}
                  onClickNext={covidPastInfections =>
                    handleNextClick(undefined, covidPastInfections)
                  }
                />
              </PageItemContainer>
            )}
            {visibleStep === 4 && (
              <PageItemContainer>
                <DiseasesStep4
                  onClickBack={() => handleBackClick()}
                  onClickNext={(userMetaData, covidComorbidities) =>
                    handleNextClick(userMetaData, undefined, covidComorbidities)
                  }
                />
              </PageItemContainer>
            )}
            {visibleStep === 5 && (
              <PageItemContainer>
                <DiseasesStep5
                  onClickNext={(userMetadata, userAce, userArb) =>
                    goToNextStep(userMetadata, userAce, userArb)
                  }
                  onClickBack={() => handleBackClick()}
                />
              </PageItemContainer>
            )}
            {visibleStep === 6 && (
              <PageItemContainer>
                <DiseasesStep6
                  onClickBack={() => handleBackClick()}
                  onClickNext={covidSymptopms =>
                    handleNextClick(
                      undefined,
                      undefined,
                      undefined,
                      covidSymptopms
                    )
                  }
                />
              </PageItemContainer>
            )}
            {visibleStep === 7 && (
              <PageItemContainer>
                <DiseasesStep7
                  onClickBack={() => handleBackClick()}
                  onClickNext={(userMetaData, diseases) =>
                    handleNextClick(
                      userMetaData,
                      undefined,
                      undefined,
                      undefined,
                      diseases
                    )
                  }
                />
              </PageItemContainer>
            )}
          </UserProfileContext.Provider>
        )}
    </Grid>
  );
}

export default OnboardingDiseasesPage;
